/**
 * SMS Campaign Excluded Contacts management page.
 * Route: /marketing/sms-campaigns/:id/excludes
 */
import { useState, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2, Plus, Trash2, Search, X, RotateCcw } from 'lucide-react';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { useToast } from '@/hooks/useToast';
import * as marketingSvc from '@/services/marketing.service';
import type { SmsCampaignExcludeContact } from '@/services/marketing.service';
import { apiClient } from '@/services/apiClient';
import { formatPhone } from '@/lib/utils';

interface PatientResult {
  patientID: number;
  firstName: string;
  lastName: string;
  email: string;
  cellPhone: string;
}

export default function SmsCampaignExcludesPage() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const qc = useQueryClient();
  const campaignId = Number(id);

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showDeleted, setShowDeleted] = useState(false);
  const perPage = 25;

  const { data: campaign } = useQuery({
    queryKey: ['marketing', 'sms-campaigns', campaignId],
    queryFn: () => marketingSvc.getSmsCampaign(campaignId),
    enabled: !!id,
  });

  const isEditable = campaign?.status === 'draft';

  const { data: excludesData, isLoading } = useQuery({
    queryKey: ['marketing', 'sms-campaigns', campaignId, 'exclude-contacts', search, page, showDeleted],
    queryFn: () => marketingSvc.getSmsCampaignExcludeContacts(campaignId, { search: search || undefined, page, perPage, showDeleted: showDeleted || undefined }),
    enabled: !!id,
  });
  const excludes = excludesData?.data ?? [];
  const total = excludesData?.meta?.total ?? 0;

  const { data: excludeCount = 0 } = useQuery({
    queryKey: ['marketing', 'sms-campaigns', campaignId, 'exclude-contact-count'],
    queryFn: () => marketingSvc.getSmsCampaignExcludeContactCount(campaignId),
    enabled: !!id,
  });

  const [showAddModal, setShowAddModal] = useState(false);
  const [patientSearch, setPatientSearch] = useState('');

  const { data: patientResults = [], isFetching: searchingPatients } = useQuery({
    queryKey: ['patient-search-sms-exclude', patientSearch],
    queryFn: async () => {
      const res = await apiClient.get('/patients/search', { params: { q: patientSearch } });
      const rows = (res.data?.data ?? []) as Array<Record<string, any>>;
      return rows.map(r => ({
        patientID: r.patientId ?? r.patientID,
        firstName: r.firstName ?? '',
        lastName: r.lastName ?? '',
        email: r.email ?? '',
        cellPhone: r.cellPhone ?? '',
      })) as PatientResult[];
    },
    enabled: patientSearch.length >= 2,
  });

  const invalidateExcludes = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['marketing', 'sms-campaigns', campaignId, 'exclude-contacts'] });
    qc.invalidateQueries({ queryKey: ['marketing', 'sms-campaigns', campaignId, 'exclude-contact-count'] });
    qc.invalidateQueries({ queryKey: ['marketing', 'sms-campaigns', campaignId, 'recipient-count'] });
  }, [qc, campaignId]);

  const addMut = useMutation({
    mutationFn: (data: { patientID?: number; phoneNumber: string; firstName?: string; lastName?: string; email?: string }) =>
      marketingSvc.addSmsCampaignExcludeContact(campaignId, data),
    onSuccess: () => { toast.success('Exclude added'); invalidateExcludes(); },
    onError: () => toast.error('Failed to add exclude'),
  });

  const removeMut = useMutation({
    mutationFn: (excludeId: number) => marketingSvc.removeSmsCampaignExcludeContact(campaignId, excludeId),
    onSuccess: () => { toast.success('Exclude removed'); invalidateExcludes(); },
    onError: () => toast.error('Failed to remove exclude'),
  });

  const restoreMut = useMutation({
    mutationFn: (excludeId: number) => marketingSvc.restoreSmsCampaignExcludeContact(campaignId, excludeId),
    onSuccess: () => { toast.success('Exclude restored'); invalidateExcludes(); },
    onError: () => toast.error('Failed to restore exclude'),
  });

  const handleAddPatient = useCallback((p: PatientResult) => {
    addMut.mutate({
      patientID: p.patientID,
      phoneNumber: p.cellPhone || '',
      firstName: p.firstName,
      lastName: p.lastName,
      email: p.email,
    });
    setShowAddModal(false);
    setPatientSearch('');
  }, [addMut]);

  const columns: DataTableColumn<SmsCampaignExcludeContact>[] = useMemo(() => [
    { key: 'phoneNumber', header: 'Phone Number', render: (row) => formatPhone(row.phoneNumber) },
    { key: 'firstName', header: 'First Name' },
    { key: 'lastName', header: 'Last Name' },
    {
      key: 'createDate', header: 'Date Added',
      render: (row) => row.createDate ? new Date(row.createDate).toLocaleDateString() : '—',
    },
    ...(isEditable ? [{
      key: 'actions' as const,
      header: '',
      render: (row: SmsCampaignExcludeContact) => {
        if (row.status === 0) {
          return (
            <button type="button" title="Restore"
              onClick={(e) => { e.stopPropagation(); restoreMut.mutate(row.smsCampaignExcludeID); }}
              className="inline-flex items-center justify-center h-7 w-7 rounded text-green-500 hover:text-green-700 hover:bg-green-50 transition-colors">
              <RotateCcw className="h-4 w-4" />
            </button>
          );
        }
        return (
          <button type="button" title="Remove"
            onClick={(e) => { e.stopPropagation(); removeMut.mutate(row.smsCampaignExcludeID); }}
            className="inline-flex items-center justify-center h-7 w-7 rounded text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors">
            <Trash2 className="h-4 w-4" />
          </button>
        );
      },
    }] : []),
  ], [isEditable, removeMut, restoreMut]);

  return (
    <div className="min-h-screen bg-gray-50">
      <style>{`.sms-exc-deleted-row { opacity: 0.5; background: #fafafa; }`}</style>

      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center gap-3 mb-1">
          <Link to={campaign?.status === 'scheduled' ? `/marketing/sms-campaigns/${id}/scheduled` : `/marketing/sms-campaigns/${id}`} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
            <ArrowLeft className="h-4 w-4" />
            Back to Campaign
          </Link>
        </div>
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold text-gray-800">Excluded Contacts</h1>
          <div className="flex items-center gap-2">
            {isEditable && (
              <button type="button" onClick={() => setShowAddModal(true)}
                className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 transition-colors">
                <Plus className="h-4 w-4" />
                Add Patient
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Count + Search + Show Deleted */}
      <div className="px-6 py-3" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 28, fontWeight: 600, color: '#dc3545' }}>{excludeCount.toLocaleString()}</span>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#777' }}>excluded</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {isEditable && (
            <label className="inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none whitespace-nowrap">
              <input type="checkbox" checked={showDeleted}
                onChange={e => { setShowDeleted(e.target.checked); setPage(1); }}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              Show Deleted
            </label>
          )}
          <div className="relative" style={{ width: 240 }}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input type="text" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search by phone or name..."
              className="w-full rounded-md border border-gray-300 pl-9 pr-3 py-2 text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none" />
          </div>
        </div>
      </div>

      <div className="px-6 pb-6">
        {isLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
        ) : (
          <DataTable<SmsCampaignExcludeContact>
            data={excludes}
            columns={columns}
            keyField="smsCampaignExcludeID"
            page={page}
            pageSize={perPage}
            total={total}
            onPageChange={setPage}
            rowClassName={(row) => row.status === 0 ? 'sms-exc-deleted-row' : ''}
          />
        )}
      </div>

      {/* Add Patient Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <h3 className="text-lg font-semibold text-gray-800">Add Patient to Exclude List</h3>
              <button type="button" onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-5 py-4">
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input type="text" value={patientSearch} onChange={e => setPatientSearch(e.target.value)}
                  placeholder="Search by name or phone (min 2 chars)..."
                  autoFocus
                  className="w-full rounded-md border border-gray-300 pl-9 pr-3 py-2 text-sm focus:border-blue-400 outline-none" />
              </div>
              <div className="max-h-60 overflow-y-auto">
                {searchingPatients ? (
                  <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
                ) : patientResults.length === 0 ? (
                  <p className="text-center py-4 text-sm text-gray-400">
                    {patientSearch.length < 2 ? 'Type at least 2 characters' : 'No patients found'}
                  </p>
                ) : (
                  patientResults.map(p => (
                    <button key={p.patientID} type="button" onClick={() => handleAddPatient(p)}
                      className="flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-red-50 rounded transition-colors">
                      <div>
                        <span className="font-medium text-gray-700">{p.firstName} {p.lastName}</span>
                        <span className="ml-2 text-gray-400">{p.cellPhone || p.email}</span>
                      </div>
                      <Plus className="h-4 w-4 text-red-500" />
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
